import { useEffect, useState, useSyncExternalStore } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, buttonProps } from "@a2z/ui";
import { useLocale } from "@/app/locale";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import {
  assertPrivateSession,
  getPrivateSessionState,
  subscribePrivateSession,
} from "@/lib/api/private-session";
import { useSessionMutation } from "@/lib/query/core/use-session-mutation";
import { getSellerPath } from "@/lib/routing/portal-urls";
import { metaLinkPath } from "@/lib/auth/safe-return";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import type { PublicSellerAuthLoginResponse } from "@/lib/api/types";
import { inspectPrivateBinding, recoverPrivateBinding } from "@/shared/services/sessionChannel";
import { useThemeStore } from "@/shared/theme/useTheme";
import { FacebookIcon, InstagramIcon } from "@/shared/components/SocialIcons";
import { changePrivateSession } from "@/shared/services/privateSessionChange";
import {
  metaIdentityErrorMessage,
  metaRequest,
  useMetaWrite,
  type MetaIdentityProvider,
} from "@/shared/meta/api";
import { parseMetaCallback } from "../lib/meta-callback";
import {
  assertNativeMetaCompletion, finishNativeMetaCallback,
  readNativeMetaCallback, type NativeMetaCallback,
} from "@/lib/native/meta-authorization";
import { isIosCompanionApp, isSellerNativeApp } from "@/lib/native/runtime";

type CallbackFeedback = {
  status: "pending" | "signed-in" | "error";
  provider: MetaIdentityProvider;
  message?: string;
  code?: string;
};

function stripMetaCallbackQuery() {
  window.history.replaceState(
    window.history.state,
    "",
    window.location.pathname,
  );
}

export function MetaCallbackPage() {
  const iosCompanion = isIosCompanionApp();
  const locale = useLocale();
  const resolvedDark = useThemeStore((state) => state.resolvedDark);
  const t = useTranslations();
  const navigate = useNavigate();
  const client = useQueryClient();
  const session = useSyncExternalStore(
    subscribePrivateSession,
    getPrivateSessionState,
  );
  const [callback] = useState(() => isSellerNativeApp()
    ? readNativeMetaCallback() ?? { ticket: null, purpose: null, provider: null }
    : parseMetaCallback(window.location.search));
  const nativeCallback = "native" in callback ? callback as NativeMetaCallback : null;
  const nativeProof = nativeCallback ? {
    nativeVerifier: nativeCallback.native.verifier,
    nativeAttemptId: nativeCallback.native.attemptId,
  } : {};
  const assertNativeBinding = async () => {
    if (!nativeCallback) return;
    const binding = await inspectPrivateBinding();
    assertNativeMetaCompletion(nativeCallback, binding ?? null);
  };
  const [connectionComplete, setComplete] = useState(false);
  const [completedConnection, setCompletedConnection] = useState<{
    id?: string;
    signInEnabled?: boolean;
  } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [recoveringSession, setRecoveringSession] = useState(false);
  const feedbackKey = ["public-meta-callback-feedback", session.generation];
  const feedback = useQuery<CallbackFeedback | null>({
    queryKey: feedbackKey,
    queryFn: () => null,
    enabled: false,
  });
  const provider = callback.provider ?? feedback.data?.provider ?? "FACEBOOK";
  const loginComplete = feedback.data?.status === "signed-in";
  const pausedSession = session.changing &&
    (feedback.data?.status === "signed-in" || feedback.data?.status === "error");
  const complete = connectionComplete || loginComplete;
  // Remove the single-use ticket before loading any private content or external links.
  useEffect(stripMetaCallbackQuery, []);
  const returnSessionKey = [
    "seller",
    "meta-callback",
    "session",
    session.generation,
  ];
  const returnSession = useQuery({
    queryKey: returnSessionKey,
    queryFn: async () => {
      stripMetaCallbackQuery();
      const generation = getPrivateSessionState().generation;
      const current = await inspectPrivateBinding();
      assertPrivateSession(generation);
      return current
        ? {
            storeSlug: current.seller.storeSlug,
            mustChangePassword: current.mustChangePassword,
            availableStoreCount: current.availableStores?.length ?? 0,
          }
        : null;
    },
    enabled: !session.changing && feedback.data?.status !== "pending",
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });
  const login = useSessionMutation({
    retry: false,
    meta: { skipGlobalErrorToast: true },
    mutationFn: async (ticket: string) => {
      await assertNativeBinding();
      const generation = getPrivateSessionState().generation + 1;
      const nextFeedbackKey = ["public-meta-callback-feedback", generation];
      client.setQueryData<CallbackFeedback>(nextFeedbackKey, {
        status: "pending",
        provider,
      });
      try {
        await changePrivateSession(client, () =>
          metaRequest<PublicSellerAuthLoginResponse>(
            "/public/meta/auth/complete",
            "POST",
            { ticket, provider, ...nativeProof },
          ),
        );
        if (getPrivateSessionState().generation !== generation) return;
        client.setQueryData<CallbackFeedback>(nextFeedbackKey, {
          status: "signed-in",
          provider,
        });
      } catch (failure) {
        if (getPrivateSessionState().generation === generation)
          client.setQueryData<CallbackFeedback>(nextFeedbackKey, {
            status: "error",
            provider,
            message: metaIdentityErrorMessage(failure, provider),
            code:
              failure instanceof ApiJsonClientError ? failure.code : undefined,
          });
        throw failure;
      }
    },
  });
  const connection = useMetaWrite(
    session.storeSlug ?? "",
    async (ticket: string) => {
      await assertNativeBinding();
      return metaRequest<{ id?: string; signInEnabled?: boolean }>(
        callback.purpose === "LINK"
          ? "/seller/meta/identity/complete"
          : "/seller/meta/connections/complete",
        "POST",
        { ticket, ...(callback.purpose === "LINK" || nativeCallback ? { provider } : {}), ...nativeProof },
      );
    },
  );
  useEffect(() => {
    if (!loginComplete || session.changing || !returnSession.data) return;
    const current = returnSession.data;
    navigate(
      current.mustChangePassword
        ? `/${locale}/change-password`
        : current.availableStoreCount > 1
          ? `/${locale}/select-store`
          : getSellerPath(locale, current.storeSlug),
      { replace: true },
    );
  }, [locale, loginComplete, navigate, returnSession.data, session.changing]);
  const busy =
    login.isPending ||
    connection.isPending ||
    recoveringSession ||
    (session.changing && !pausedSession) ||
    feedback.data?.status === "pending" ||
    (loginComplete && returnSession.isPending && !pausedSession);
  const retrySession = async () => {
    if (!pausedSession) {
      await returnSession.refetch();
      return;
    }
    const generation = session.generation;
    if (getPrivateSessionState().generation !== generation) return;
    setRecoveringSession(true);
    try {
      await recoverPrivateBinding(client, generation);
    } catch {
      // Keep private requests paused and retry only the session read, never the ticket.
    } finally {
      setRecoveringSession(false);
    }
  };
  const confirm = async () => {
    if (!callback.ticket || !callback.purpose || !callback.provider ||
      (iosCompanion && callback.purpose !== "BUSINESS_CONNECT")) return;
    setError(null);
    try {
      if (callback.purpose === "LOGIN")
        await login.mutateAsync(callback.ticket);
      else {
        const result = await connection.mutateAsync(callback.ticket);
        setCompletedConnection(result);
        setComplete(true);
        void returnSession.refetch();
      }
    } catch (failure) {
      setError(failure);
    } finally {
      if (nativeCallback) finishNativeMetaCallback(nativeCallback.native.attemptId);
    }
  };
  const activeStore = returnSession.data?.storeSlug;
  useEffect(() => {
    if (
      !connectionComplete ||
      callback.purpose !== "BUSINESS_CONNECT" ||
      !completedConnection?.id ||
      !activeStore ||
      returnSession.isFetching ||
      returnSession.isError ||
      returnSession.data?.mustChangePassword
    )
      return;
    navigate(
      `/${locale}/${encodeURIComponent(activeStore)}/meta?tab=connections&connection=${encodeURIComponent(completedConnection.id)}`,
      { replace: true },
    );
  }, [
    activeStore,
    callback.purpose,
    completedConnection,
    connectionComplete,
    locale,
    navigate,
    returnSession.data?.mustChangePassword,
    returnSession.isError,
    returnSession.isFetching,
  ]);
  // Only a completed, server-validated LINK selects Profile. Error query values
  // never choose a destination or supply a store identity.
  const returnToProfile = complete && callback.purpose === "LINK";
  const destination = returnSession.data?.mustChangePassword
    ? `/${locale}/change-password`
    : activeStore
      ? `/${locale}/${encodeURIComponent(activeStore)}/${returnToProfile ? "profile" : "meta?tab=connections"}`
      : `/${locale}/login`;
  const returnLabel = t(
    returnSession.data?.mustChangePassword
      ? keys.auth.firstPasswordTitle
      : activeStore
        ? returnToProfile
          ? keys.meta.callbackReturnProfile
          : keys.meta.callbackReturnConnections
        : keys.meta.callbackReturnLogin,
  );
  const validCallback = Boolean(
    callback.ticket && callback.purpose && callback.provider &&
    (!iosCompanion || callback.purpose === "BUSINESS_CONNECT"),
  );
  const failure =
    !complete &&
    (!validCallback || Boolean(error) || feedback.data?.status === "error");
  const unlinked = !iosCompanion && (
    (error instanceof ApiJsonClientError &&
      error.code === "META_IDENTITY_NOT_LINKED") ||
    feedback.data?.code === "META_IDENTITY_NOT_LINKED");
  const linkRecoveryPath = metaLinkPath(locale, provider);
  const checkingSession = !pausedSession && (returnSession.isPending || returnSession.isFetching);
  const failureMessage = error
    ? metaIdentityErrorMessage(error, provider)
    : feedback.data?.message || t(keys.meta.callbackFailed);
  const heading = complete
    ? callback.purpose === "LINK"
      ? keys.meta.identityLinkSuccessTitle
      : keys.meta.callbackSuccessTitle
    : unlinked && !busy
      ? keys.meta.identityRecoveryTitle
      : failure && !busy
        ? keys.meta.callbackFailureTitle
        : keys.meta.callbackConfirmTitle;
  return (
    <main className="flex min-h-svh items-center justify-center bg-[color:var(--canvas)] px-4 py-10 text-[color:var(--text-primary)] sm:px-6">
      <div className="w-full max-w-[480px] space-y-6">
        <div className="flex justify-center">
          <img
            src={resolvedDark ? "/logo-sidebar-dark.svg" : "/logo-sidebar.svg"}
            alt={t(keys.common.appName)}
            className="h-7 w-auto"
          />
        </div>
        <section
          aria-labelledby="meta-callback-title"
          className="overflow-hidden rounded-2xl border border-[color:var(--border-light)] bg-[color:var(--surface)]"
        >
          <div className="flex items-center justify-between gap-4 border-b border-[color:var(--border-light)] bg-[color:var(--surface-alt)] px-6 py-4">
            <span className="text-sm font-semibold">{t(keys.meta.title)}</span>
            <div
              className="flex items-center gap-3 text-[color:var(--text-muted)]"
              aria-hidden="true"
            >
              <FacebookIcon />
              <span className="h-4 w-px bg-[color:var(--border-strong)]" />
              <InstagramIcon />
            </div>
          </div>
          <div className="space-y-5 px-6 py-8 text-center sm:px-8">
            <div
              aria-hidden="true"
              className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full ${complete ? "bg-[color:var(--success-100)] text-[color:var(--success-text)]" : failure && !busy && !unlinked ? "bg-[color:var(--warning-100)] text-[color:var(--warning-700)]" : "bg-[color:var(--surface-alt)] text-[color:var(--text-primary)]"}`}
            >
              {unlinked && !busy ? (
                <span className="[&_svg]:h-8 [&_svg]:w-8">
                  {provider === "FACEBOOK" ? (
                    <FacebookIcon />
                  ) : (
                    <InstagramIcon />
                  )}
                </span>
              ) : (
                <svg
                  viewBox="0 0 24 24"
                  className="h-8 w-8"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  focusable="false"
                >
                  <circle cx="12" cy="12" r="9" />
                  {complete ? (
                    <path d="m8 12 3 3 5-6" />
                  ) : failure && !busy && !unlinked ? (
                    <>
                      <path d="M12 7v6" />
                      <path d="M12 17h.01" />
                    </>
                  ) : (
                    <>
                      <path d="M12 7v5l3 2" />
                    </>
                  )}
                </svg>
              )}
            </div>
            <h1
              id="meta-callback-title"
              className="text-2xl font-semibold leading-snug"
            >
              {t(heading, {
                provider: t(
                  provider === "FACEBOOK"
                    ? keys.meta.providerFacebook
                    : keys.meta.providerInstagram,
                ),
              })}
            </h1>
            {busy ? (
              <p
                role="status"
                className="text-sm leading-7 text-[color:var(--text-muted)]"
              >
                {t(keys.meta.callbackLoading)}
              </p>
            ) : complete ? (
              <p
                role="status"
                className="text-sm leading-7 text-[color:var(--text-muted)]"
              >
                {t(
                  callback.purpose === "LINK"
                    ? keys.meta.identityLinkSuccessHint
                    : callback.purpose === "BUSINESS_CONNECT"
                      ? iosCompanion
                        ? keys.meta.callbackComplete
                        : completedConnection?.signInEnabled
                          ? keys.meta.unifiedConnectionSuccess
                          : keys.meta.businessConnectionSuccessHint
                      : keys.meta.callbackComplete,
                )}
              </p>
            ) : failure ? (
              <p
                role={unlinked ? "status" : "alert"}
                className="text-sm leading-7 text-[color:var(--text-muted)]"
              >
                {unlinked ? t(keys.meta.identityRecoveryHint) : failureMessage}
              </p>
            ) : (
              <p className="text-sm leading-7 text-[color:var(--text-muted)]">
                {t(
                  callback.purpose === "LINK"
                    ? provider === "INSTAGRAM"
                      ? keys.meta.linkInstagramConfirm
                      : keys.meta.linkConfirm
                    : callback.purpose === "LOGIN"
                      ? provider === "INSTAGRAM"
                        ? keys.meta.instagramLoginConfirm
                        : keys.meta.facebookLoginConfirm
                      : keys.meta.callbackConfirm,
                )}
              </p>
            )}
          </div>
          <div className="space-y-3 border-t border-[color:var(--border-light)] px-6 py-5 sm:px-8">
            {busy ? (
              <Button variant="primary" size="touch" fullWidth isLoading>
                {t(keys.meta.callbackLoading)}
              </Button>
            ) : (
              <>
                {unlinked ? (
                  <Link
                    to={linkRecoveryPath}
                    onClick={() => { if (nativeCallback) finishNativeMetaCallback(nativeCallback.native.attemptId); }}
                    {...buttonProps({
                      variant: "primary",
                      size: "touch",
                      fullWidth: true,
                    })}
                  >
                    {t(keys.meta.identityRecoveryAction)}
                  </Link>
                ) : null}
                {validCallback && !complete && !failure ? (
                  <Button
                    variant="primary"
                    size="touch"
                    fullWidth
                    onClick={() => void confirm()}
                  >
                    {t(
                      callback.purpose === "LOGIN"
                        ? keys.meta.callbackLoginContinue
                        : keys.meta.callbackContinue,
                    )}
                  </Button>
                ) : null}
                {checkingSession ? (
                  <div role="status">
                    <Button variant="outline" size="touch" fullWidth isLoading>
                      {t(keys.meta.callbackCheckingSession)}
                    </Button>
                  </div>
                ) : pausedSession || returnSession.isError ||
                  (loginComplete && returnSession.data === null) ? (
                  <div className="space-y-3">
                    <p
                      role="alert"
                      className="text-center text-sm leading-6 text-[color:var(--text-muted)]"
                    >
                      {t(keys.meta.callbackSessionUnavailable)}
                    </p>
                    <Button
                      variant={
                        (failure && !unlinked) || complete
                          ? "primary"
                          : "outline"
                      }
                      size="touch"
                      fullWidth
                      onClick={() => void retrySession()}
                    >
                      {t(keys.common.retry)}
                    </Button>
                  </div>
                ) : (
                  <Link
                    to={destination}
                    onClick={() => { if (nativeCallback) finishNativeMetaCallback(nativeCallback.native.attemptId); }}
                    {...buttonProps({
                      variant:
                        (failure && !unlinked) || complete
                          ? "primary"
                          : "outline",
                      size: "touch",
                      fullWidth: true,
                    })}
                  >
                    {returnLabel}
                  </Link>
                )}
                {!iosCompanion && connectionComplete &&
                callback.purpose === "BUSINESS_CONNECT" &&
                !completedConnection?.signInEnabled ? (
                  <Link
                    to={linkRecoveryPath}
                    {...buttonProps({
                      variant: "outline",
                      size: "touch",
                      fullWidth: true,
                    })}
                  >
                    {t(keys.meta.identitySetupAction, {
                      provider: t(
                        provider === "FACEBOOK"
                          ? keys.meta.providerFacebook
                          : keys.meta.providerInstagram,
                      ),
                    })}
                  </Link>
                ) : null}
              </>
            )}
          </div>
        </section>
      </div>
    </main>
  );
}
